// StorageDiagnostic.js
import React, { useState, useEffect } from 'react';
import { getStorage, ref, listAll } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

function StorageDiagnostic() {
  const [diagnosticInfo, setDiagnosticInfo] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const runDiagnostics = async () => {
      const info = {
        timestamp: new Date().toISOString(),
        authenticated: false,
        authChecked: false,
        storageAccessed: false,
        errors: []
      };
      
      try {
        // Check authentication
        const auth = getAuth();
        info.authChecked = true;
        
        if (auth.currentUser) {
          info.authenticated = true;
          info.userId = auth.currentUser.uid;
          info.email = auth.currentUser.email;
          
          // Force token refresh to ensure we have the latest permissions
          await auth.currentUser.getIdToken(true);
          info.tokenRefreshed = true;
          
          // Try accessing storage
          const storage = getStorage();
          info.storageInitialized = true;
          
          try {
            // Try to access root first
            const rootRef = ref(storage);
            await listAll(rootRef);
            info.rootAccessSucceeded = true;
            
            // Try to access documents folder
            try {
              const docsRef = ref(storage, 'documents');
              const docsList = await listAll(docsRef);
              info.documentsAccessSucceeded = true;
              info.documentsCount = docsList.items.length;
            } catch (docsError) {
              info.documentsAccessError = docsError.message;
              info.documentsErrorCode = docsError.code;
              info.errors.push({
                location: "documents folder",
                error: docsError.message,
                code: docsError.code
              });
            }
          } catch (rootError) {
            info.rootAccessError = rootError.message;
            info.rootErrorCode = rootError.code;
            info.errors.push({
              location: "root storage",
              error: rootError.message,
              code: rootError.code
            });
          }
        } else {
          info.authenticated = false;
          info.errors.push({
            location: "authentication",
            error: "User not authenticated"
          });
        }
      } catch (error) {
        info.errors.push({
          location: "general",
          error: error.message,
          code: error.code
        });
      }
      
      setDiagnosticInfo(info);
      setLoading(false);
    };
    
    runDiagnostics();
  }, []);
  
  if (loading) {
    return <div className="p-4">Running storage diagnostics...</div>;
  }
  
  return (
    <div className="p-4 bg-gray-100 rounded-lg mt-4">
      <h3 className="text-lg font-bold mb-3">Storage Diagnostics</h3>
      
      {diagnosticInfo.authenticated ? (
        <div className="mb-2 text-green-700">✓ Authenticated as {diagnosticInfo.email}</div>
      ) : (
        <div className="mb-2 text-red-700">✗ Not authenticated</div>
      )}
      
      {diagnosticInfo.tokenRefreshed && (
        <div className="mb-2 text-green-700">✓ Auth token refreshed</div>
      )}
      
      {diagnosticInfo.rootAccessSucceeded ? (
        <div className="mb-2 text-green-700">✓ Root storage access succeeded</div>
      ) : (
        <div className="mb-2 text-red-700">✗ Root storage access failed</div>
      )}
      
      {diagnosticInfo.documentsAccessSucceeded ? (
        <div className="mb-2 text-green-700">
          ✓ Documents folder access succeeded ({diagnosticInfo.documentsCount} items)
        </div>
      ) : (
        <div className="mb-2 text-red-700">
          ✗ Documents folder access failed: {diagnosticInfo.documentsAccessError}
        </div>
      )}
      
      {diagnosticInfo.errors.length > 0 && (
        <div className="mt-4">
          <h4 className="font-bold mb-2">Errors:</h4>
          <pre className="bg-gray-800 text-white p-3 rounded text-sm overflow-auto">
            {JSON.stringify(diagnosticInfo.errors, null, 2)}
          </pre>
        </div>
      )}
      
      <div className="mt-4">
        <button 
          onClick={() => window.location.reload()}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Run Again
        </button>
      </div>
    </div>
  );
}

export default StorageDiagnostic;